<template>
  <div v-if="user">
    <b-row>
      <b-col>
        <div class="page-subtitle mt-5 mb-4">Login Details</div>
        <b-form-group
          id="input-group-email"
          label-for="input-email"
          class="font-weight-bold"
        >
          <!-- Label -->
          <template #label>
            Email Address
            <span v-if="!user.email_verified" style="color: red">
              (Verification Required)
            </span>
          </template>

          <!-- Change -->
          <div class="input-link" @click="change('email')">
            <b-link>Change</b-link>
          </div>

          <!-- Verify -->
          <div
            v-if="!user.email_verified"
            class="input-link-2"
            @click="verify('email')"
          >
            <b-link style="color: red">Verify</b-link>
          </div>
          <div v-else class="input-link-3" style="color: limegreen">
            VERIFIED
          </div>

          <!-- Display -->
          <b-form-input
            id="input-email"
            type="text"
            disabled
            v-model="user.email"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          id="input-group-phone"
          label-for="input-phone"
          class="font-weight-bold"
        >
          <!-- Label -->
          <template #label>
            Mobile Number
            <span v-if="!user.phone_number_verified" style="color: red">
              (Verification Required)
            </span>
          </template>

          <!-- Change -->
          <div
            v-if="user.phone_number"
            class="input-link"
            @click="change('phone_number')"
          >
            <b-link>Change</b-link>
          </div>

          <!-- Verify -->
          <!-- prettier-ignore -->
          <div v-if="!user.phone_number_verified" class="input-link-2" @click="
            user.phone_number
              ? verify('phone_number')
              : change('phone_number')">
            <b-link style="color: red">Verify</b-link>
          </div>
          <div v-else class="input-link-3" style="color: limegreen">
            VERIFIED
          </div>

          <!-- Display -->
          <b-form-input
            id="input-phone"
            type="text"
            disabled
            :value="phoneDisplayText"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          id="input-group-password"
          label="Password"
          label-for="input-password"
          class="font-weight-bold"
        >
          <!-- Change -->
          <div class="input-link" @click="change('password')">
            <b-link>Change</b-link>
          </div>

          <!-- Display -->
          <b-form-input
            id="input-password"
            type="password"
            disabled
            placeholder="••••••••••••••••"
            v-model="user.password"
          ></b-form-input>
        </b-form-group>

        <!-- Rest of the form, hidden when unverified -->
        <template v-if="userVerified">
          <div class="page-subtitle mt-5 mb-4">Notifications</div>
          <div>
            <b-form-group>
              <b-form-checkbox
                switch
                size="lg"
                v-model="emailEnabled"
                @change="
                  changeSetting('email_notification_enable', emailEnabled)
                "
                ><label class="switch-label font-weight-bold"
                  >Email notifications</label
                ></b-form-checkbox
              >
            </b-form-group>
          </div>

          <template v-if="!hideReminders">
            <div class="page-subtitle mt-5 mb-4">Reminders</div>
            <div>
              <b-dropdown
                variant="outlined"
                :text="!reminderSetting ? 'Select Reminder' : reminderSetting"
                class="dropdown m-2 gray--3"
              >
                <b-dropdown-item
                  v-for="sched in scheduleList"
                  :key="sched.id"
                  :value="sched"
                  class="align-middle"
                  @click="changeSetting('expiring_schedule_id', sched.id)"
                  >{{ sched.label }}</b-dropdown-item
                >
              </b-dropdown>
            </div>
          </template>
        </template>

        <b-alert
          class="custom-alert"
          :show="dismissCountDown"
          dismissible
          fade
          variant="primary"
          @dismiss-count-down="countDownChanged"
          >Notifications setting is successfully saved.</b-alert
        >
      </b-col>
      <b-col class="d-none d-lg-block"></b-col>
    </b-row>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import { phoneDisplayParts } from '@/utils/phonePolicy'

export default {
  props: {
    hideReminders: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dismissCountDown: 0,
      pushConversion: false,
    }
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      scheduleList: (state) => state.notification.scheduleList,
    }),
    ...mapGetters('notification', [
      'emailNotificationSetting',
      'reminderSetting',
    ]),
    userVerified() {
      return this.user.email_verified && this.user.phone_number_verified
    },
    phoneDisplayText() {
      const parts = phoneDisplayParts(this.user.phone_number)
      return parts
        ? `${parts.dialCode} ${parts.number}`
        : this.user.phone_number
    },
    emailEnabled: {
      get() {
        return this.emailNotificationSetting
      },
      set(value) {
        this.setEmailNotificationSetting(value)
      },
    },
  },
  watch: {
    emailNotificationSetting(value) {
      this.emailEnabled = value
    },
  },

  methods: {
    ...mapActions({
      getNotificationSettings: 'notification/getNotificationSettings',
      getScheduleList: 'notification/getScheduleList',
    }),
    ...mapMutations({
      setReminderSetting: 'notification/setReminderSetting',
      setEmailNotificationSetting: 'notification/setEmailNotificationSetting',
    }),
    change(field) {
      this.$router.push({
        name: 'user.verify-page',
        params: { field: field },
      })
    },
    verify(field) {
      this.$router.push({
        name: 'user.verify-only-page',
        params: { field: field },
      })
    },
    async changeSetting(setting, value) {
      let formData = {
        [setting]: value,
      }

      await this.$api
        .put(`notifications/settings`, formData)
        .then(({ data }) => {
          if (data.success) {
            this.setEmailNotificationSetting(
              data.data.email_notification_enable,
            )
            this.setReminderSetting(data.data.expiring_schedule_id.label)
            this.showAlert()
          }
        })
    },
    showAlert() {
      this.dismissCountDown = 5
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    pushSignupVisit() {
      const u = this.user
      const isUnverified = u && (!u.email_verified || !u.phone_number_verified)
      const fromSignup = sessionStorage.getItem('fromSignup') === '1'
      const shouldPush = fromSignup && isUnverified && !this.pushConversion

      // always clear so it can't bleed into later logins/sessions
      if (fromSignup) {
        sessionStorage.removeItem('fromSignup')
      }
      if (!shouldPush) return

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'account_form_view',
        })
      }
      this.pushConversion = true
    },
  },
  created() {
    this.pushSignupVisit()
    this.getNotificationSettings()
    this.getScheduleList()
  },
}
</script>

<style lang="scss" scoped>
.form-control:disabled,
.form-control[readonly] {
  background-color: white;
}

// ── Mobile responsiveness ───────────────────────────────────────────────
// Stack the bare equal-width columns full-width below the `md` breakpoint so
// the content isn't squeezed into half the row, and scale the section-header
// spacing so gaps start compact on small screens and build up to the current
// desktop values. Laptop layout is unchanged.
@media (max-width: 767.98px) {
  .col {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
@media (max-width: 575.98px) {
  .page-subtitle.mt-5 {
    margin-top: 1.25rem !important;
  }
  .page-subtitle.mt-4 {
    margin-top: 0.75rem !important;
  }
}
@media (min-width: 576px) and (max-width: 991.98px) {
  .page-subtitle.mt-5 {
    margin-top: 2rem !important;
  }
  .page-subtitle.mt-4 {
    margin-top: 1.15rem !important;
  }
}

.input-link {
  position: absolute;
  right: 23%;
  margin-top: 13px;
}

.input-link-2 {
  position: absolute;
  right: 8%;
  margin-top: 13px;
}

.input-link-3 {
  position: absolute;
  right: 5%;
  margin-top: 13px;
}

// Phones: the links overlap the field value and each other when positioned over
// the narrow input. Reserve a strip below the input and anchor the links there
// with distinct offsets. Placed after the base rules above so these win (media
// queries don't raise specificity, so source order decides).
@media (max-width: 575.98px) {
  #input-group-email,
  #input-group-phone,
  #input-group-password {
    position: relative;
    padding-bottom: 34px;
  }
  .input-link,
  .input-link-2,
  .input-link-3 {
    top: auto;
    bottom: 4px;
    margin-top: 0;
  }
  .input-link {
    right: 108px;
  }
  .input-link-2,
  .input-link-3 {
    right: 12px;
  }
  // Keep the notifications toggle label on one line — the default 20px gap
  // pushes it to wrap next to the switch on narrow phones.
  .switch-label {
    padding-left: 12px;
    font-size: 15px;
  }
}

.switch-label {
  font-size: 16px;
  padding-left: 20px;
}

.dropdown {
  width: 100%;

  &:hover {
    border: 2px solid #4694ff;
    border-radius: 8px;
  }
}

/deep/ .dropdown-menu {
  width: 100%;
  border-radius: 8px;
  margin-top: 0;
  padding-top: 2px;
  padding-bottom: 0;
}

/deep/ .dropdown-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/deep/ .dropdown-item {
  width: 100%;
  height: 52px;
  border-bottom: 2px solid #e5e5e5;
  display: flex;
  align-items: center;
}
</style>
